import get from 'lodash/get';
import { isEqual } from 'lodash';
import { useTranslation } from '@packages/utils';
import { Modal } from 'components/Base/Modal';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { useEditEventParentMutation } from 'redux/apiSlice/eventParents.slice';
import { TValidateFieldsError } from 'data/types/generic.types';
import useForm from 'hooks/useForm';
import {
  TEditEventFamilyFormUI,
  TEditEventFamilyModalProps,
} from './EditEventFamilyModal.types';
import EditEventFamilyForm from './EditEventFamilyForm';

const eventFamilyListKey = 'descriptions.admin-portal.family.event-family-list';

const EditEventFamilyModal = ({
  open,
  setOpen,
  familyData,
}: TEditEventFamilyModalProps) => {
  const { t } = useTranslation({
    keyPrefix: eventFamilyListKey.concat('.actions.edit-family.modal'),
  });
  const { showToastNotification } = useShowToastNotification();
  const { form } = useForm<TEditEventFamilyFormUI>();
  const [updateEventParent, { isLoading }] = useEditEventParentMutation();

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { email, firstName, lastName, phone } = familyData;
      if (isEqual(values, { email, firstName, lastName, phone })) {
        handleClose();
        return;
      }

      await updateEventParent({
        ...values,
        registrantId: familyData.registrantId,
        eventId: familyData.eventId,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE', 'Family updated successfully'),
        type: ENotificationType.SUCCESS,
      });
      handleClose();
    } catch (e) {
      if (!(e as TValidateFieldsError)?.errorFields) {
        const message = get(e, 'data.message');
        showToastNotification({
          message,
          type: ENotificationType.ERROR,
        });
      }
    }
  };

  return (
    <Modal
      title={t('TITLE', 'Edit Family')}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={t('SUBMIT_BTN', 'Save')}
      cancelText={t('CANCEL_BTN', 'Cancel')}
      confirmLoading={isLoading}
    >
      <EditEventFamilyForm form={form} data={familyData} />
    </Modal>
  );
};

export default EditEventFamilyModal;
